/*
 * MovieController - showtime/film listing controller for Celebration Cinema.
 *
 * Used by (search before removing anything here):
 *   - Film/Detail.Custom.cshtml            (film detail: init + filteredItemsByFilm)
 *   - Cinema/Detail.MovieShowtimes.cshtml  (cinema detail: init + filteredItemsCinemaLocation)
 *   - Celebration_SJOData/Homepage.cshtml  (homepage: lazyLoadSJOHomepage + filteredItemsHomePage)
 *   - SitefinityJavascriptObjects/Detail.AllShowtimes(.Delayed).cshtml (legacy: init + filteredItemsHomePage)
 *   - Showtime/List.HomePage.cshtml        (legacy: init + filteredItemsCinemaLocation)
 *   - DoubleFeature/Detail.Showtimes.cshtml (double feature: init + filteredItemsDoubleFeature)
 *
 * Bundled by gulpfile.js into ShowtimeBundle.min.js and CelebrationScripts.min.js.
 *
 * History note: the old vis.js timeline experiment (TIMELINE/ITEMS, publishTimeline,
 * ngVis, timeline-demo, genreData/rivertownData demo factories) was removed 2026-06.
 * Showtime/List.Timeline.cshtml depended on members that were never populated at
 * runtime, so it was already non-functional before the removal.
 */
(function () {
    angular.module('st').requires.push('cstudioApp.services');
    angular.module('st').requires.push('cstudioApp.filters');
    angular.module('st').requires.push('cstudioApp.directives');
    angular.module('st').requires.push('cstudioApp.controllers');
    angular.module('st').requires.push('ui.bootstrap');

    angular.module("cstudioApp.controllers", ['ngAnimate']);
    angular.module("cstudioApp.services", []);
    angular.module("cstudioApp.filters", []);
    angular.module("cstudioApp.directives", []);

    function MovieController($scope, $compile, $timeout, $http, $q) {
        var mvm = this;

        //========================================================================
        // State
        //========================================================================
        mvm.CURRENT_DATE = moment().format('MMM Do YYYY');
        mvm.CURRENT_DATE_URL = moment().format('YYYY-MM-DD');
        mvm.showTimeline = false;       // legacy flag - views still bind ng-hide to it
        mvm.buildingTimeline = true;    // "loading" spinner flag
        mvm.filteredItemsVariable = [];
        mvm.dates = [];                 // ['YYYY-MM-DD', ...] selectable dates
        mvm.dateArray = [];             // [{Text, Moment, ID, StringDate}, ...] calendar strip
        mvm.Datelimit = 0;              // calendar strip paging offset
        mvm.hasShowTimes = false;
        mvm.filterByPlayingType = 'nowplaying';
        mvm.NowPlayingTab = true;
        mvm.isNowPlaying = true;
        mvm.isComingSoon = false;
        mvm.isLoading = true;
        mvm.sortorder = "+FilmTitle";
        mvm.SHOWTIMES_WITH_STATUSES = {};
        mvm.showtimeSelectedDate = moment().format('YYYY-MM-DD');

        // Canonical multi-format selection (see the availability section below).
        // Empty array = "all formats".  mvm.filterByMovieFormat is the scalar mirror
        // used only as the ng-model for the single-select homepage + legacy views.
        mvm.selectedFormats = [];
        mvm.filterByMovieFormat = undefined;

        var ORIGINAL_MOVIES = [];

        //========================================================================
        // Filter option availability (graying out impossible combos)
        //========================================================================
        // Every (CinemaID, FormatCode) pair that has at least one showing on the
        // currently selected date.  Rebuilt by each filteredItems* builder; the
        // Location/Format dropdowns use it via ng-options "disable when" so users
        // can see the full option list but not select combos with zero results.
        mvm.availablePairs = [];

        // True when a date change was initiated BY THE USER (calendar strip or
        // datepicker).  Filters that no longer have results are then auto-cleared.
        // Deliberately NOT set on initial load: deep links from events
        // (?format=IMAX...) must keep their filter and show "no showtimes" instead
        // of silently resetting.
        var resetUnavailableFiltersOnNextBuild = false;

        // Format matching is EXACT: a showtime matches when its FormatCode is one of
        // mvm.selectedFormats (OR semantics across the set).  The old IMAX / C Premium
        // family expansion was removed 2026-08 - selecting "IMAX 70mm" now shows only
        // IMAX 70mm, so a homepage pick and the film-detail page it links to agree.
        // Events that want several formats on one poster pass a comma-delimited
        // ?format=IMAX 2D,IMAX 3D,IMAX 70mm instead of relying on expansion.

        // Whether the location filter participates in availability checks.
        // False on Cinema Detail: that page has no location dropdown and ignores
        // ?cinema= in its results, so a stray cinema param from a shared/marketing
        // URL must not gray out its format options.  Set by each builder.
        var availabilityUsesLocation = true;

        // Exact format comparison (trims both sides).
        function formatEquals(a, b) {
            return String(a).trim() === String(b).trim();
        }

        // True when a showtime's FormatCode satisfies the current selection.
        // Empty selection = every format passes (OR semantics across the set).
        mvm.formatMatchesSelection = function (formatCode) {
            if (!mvm.selectedFormats || !mvm.selectedFormats.length) return true;
            return _.some(mvm.selectedFormats, function (f) { return formatEquals(f, formatCode); });
        };

        // --- Selection helpers used by the multi-select dropdown directive ------
        mvm.isFormatSelected = function (format) {
            return _.some(mvm.selectedFormats, function (f) { return formatEquals(f, format); });
        };

        // Toggle one format in/out of the selection.  Mutates the array IN PLACE so
        // every holder of the reference (directive binding, availability checks)
        // stays in sync.  Keeps the scalar mirror pointed at the first selection.
        mvm.toggleFormat = function (format) {
            var trimmed = String(format).trim();
            var idx = _.findIndex(mvm.selectedFormats, function (f) { return formatEquals(f, trimmed); });
            if (idx > -1) {
                mvm.selectedFormats.splice(idx, 1);
            } else {
                mvm.selectedFormats.push(trimmed);
            }
            mvm.filterByMovieFormat = mvm.selectedFormats.length ? mvm.selectedFormats[0] : undefined;
        };

        // Homepage/legacy single-select bridge: the <select> ng-model writes the
        // scalar mvm.filterByMovieFormat; this mirrors it into the canonical array.
        mvm.onSingleFormatChange = function () {
            mvm.selectedFormats = mvm.filterByMovieFormat ? [String(mvm.filterByMovieFormat).trim()] : [];
            mvm.AOSRefresh();
        };

        // Homepage poster ng-repeat predicate (| filter: mvm.homepageFormatFilter).
        // Array-driven so the homepage is already multi-select ready even though its
        // dropdown is still a single <select>.
        mvm.homepageFormatFilter = function (item) {
            return mvm.formatMatchesSelection(item.FormatCode);
        };

        // ---------------------------------------------------------------------------
        // Cinematic poster badge (Homepage.Cinematic template). Returns the film's
        // highest-precedence special format label, or '' for none. Precedence:
        //   1) IMAX 70MM   2) C PREMIUM XL (any, must include XL)
        //   3) C PREMIUM (any)   4) IMAX (any)
        // FormatCode here is the display string (e.g. "IMAX 70mm", "C Premium XL 2D").
        // If a special format is currently selected in the filter and this film plays
        // that tier, the badge shows that tier instead (so it never contradicts the filter).
        // To change wording or precedence, edit CINEMATIC_FORMAT_TIERS below.
        // Precedence: IMAX 70MM > C Premium XL (must incl. XL) > IMAX (any) > C Premium (any)
        var CINEMATIC_FORMAT_TIERS = [
            { rank: 1, label: 'IMAX 70MM', test: function (s) { return s.indexOf('imax') > -1 && s.indexOf('70') > -1; } },
            { rank: 2, label: 'C Premium XL', test: function (s) { return s.indexOf('c premium') > -1 && s.indexOf('xl') > -1; } },
            { rank: 3, label: 'IMAX', test: function (s) { return s.indexOf('imax') > -1; } },
            { rank: 4, label: 'C Premium', test: function (s) { return s.indexOf('c premium') > -1; } }
        ];
        function cinematicTier(formatString) {
            var s = String(formatString || '').toLowerCase();
            for (var i = 0; i < CINEMATIC_FORMAT_TIERS.length; i++) {
                if (CINEMATIC_FORMAT_TIERS[i].test(s)) { return CINEMATIC_FORMAT_TIERS[i]; }
            }
            return null;
        }
        var cinematicBadgeCache = {};
        var cinematicBadgeSrcRef = null;
        mvm.clearCinematicBadgeCache = function () { cinematicBadgeCache = {}; };
        mvm.cinematicFormatBadge = function (scheduledFilmId) {
            // Scan the FLAT homepage showtime list (same array the ng-repeat reads
            // item.FormatCode / ScheduledFilmId off of), not the nested mvm.movies.
            var src = mvm.filteredItemsVariable;
            if (!scheduledFilmId || !src) { return ''; }
            // drop the cache when the showtime set changes (e.g. date/day switch)
            if (src !== cinematicBadgeSrcRef) { cinematicBadgeCache = {}; cinematicBadgeSrcRef = src; }
            var selSig = (mvm.selectedFormats || []).join('|');
            var cacheKey = scheduledFilmId + '::' + selSig;
            if (cinematicBadgeCache.hasOwnProperty(cacheKey)) { return cinematicBadgeCache[cacheKey]; }

            // the distinct format tiers this film actually plays in
            var filmTiers = [];
            _.each(src, function (m) {
                if (m.ScheduledFilmId === scheduledFilmId && m.FormatCode) {
                    var t = cinematicTier(m.FormatCode);
                    if (t && filmTiers.indexOf(t) === -1) { filmTiers.push(t); }
                }
            });
            function lowestRank(tiers) {
                var best = tiers[0];
                for (var i = 1; i < tiers.length; i++) { if (tiers[i].rank < best.rank) { best = tiers[i]; } }
                return best;
            }
            var result = '';
            if (filmTiers.length) {
                // top-precedence tier the film plays
                result = lowestRank(filmTiers).label;
                // filter-aware swap: if the user picked a special format this film has, badge it
                if (mvm.selectedFormats && mvm.selectedFormats.length) {
                    var selTiers = [];
                    _.each(mvm.selectedFormats, function (sel) {
                        var st = cinematicTier(sel);
                        if (st && _.some(filmTiers, function (ft) { return ft.rank === st.rank; })) { selTiers.push(st); }
                    });
                    if (selTiers.length) { result = lowestRank(selTiers).label; }
                }
            }
            cinematicBadgeCache[cacheKey] = result;
            return result;
        };

        // Would selecting this format show at least one result, given the selected
        // date and (when set) the selected location?  Exact FormatCode match.
        mvm.isFormatOptionAvailable = function (format) {
            var cinema = (availabilityUsesLocation && mvm.filterByCinemaLocation) ? String(mvm.filterByCinemaLocation).trim() : null;
            return _.some(mvm.availablePairs, function (p) {
                if (cinema && p.CinemaID !== cinema) return false;
                return formatEquals(p.FormatCode, format);
            });
        };

        // Would selecting this location show at least one result, given the selected
        // date and the current format selection (OR across the selected formats)?
        mvm.isCinemaOptionAvailable = function (cinemaId) {
            cinemaId = String(cinemaId).trim();
            return _.some(mvm.availablePairs, function (p) {
                if (p.CinemaID !== cinemaId) return false;
                return mvm.formatMatchesSelection(p.FormatCode);
            });
        };

        //--- Option labels (mobile support) -------------------------------------
        // Native mobile pickers (iOS wheel / Android sheet) barely show the
        // disabled/grayed state, so unavailable options also carry a minimal
        // "(none)" text marker - plain text is the only thing guaranteed to
        // survive every client's own select UI.  Desktop additionally grays +
        // italicizes via the option:disabled CSS.
        mvm.formatOptionLabel = function (format) {
            return mvm.isFormatOptionAvailable(format) ? format : format + ' (none)';
        };

        mvm.cinemaOptionLabel = function (cinemaItem) {
            return mvm.isCinemaOptionAvailable(cinemaItem.VistaID) ? cinemaItem.Title : cinemaItem.Title + ' (none)';
        };

        // Empty-state escape hatch: clears location + format only.  The selected
        // date/calendar position is deliberately left alone.
        mvm.clearFilters = function () {
            mvm.filterByCinemaLocation = undefined;
            mvm.filterByMovieFormat = undefined;
            mvm.selectedFormats = [];
            mvm.AOSRefresh();
        };

        // Rebuilds availablePairs from the selected date's showtimes, and clears any
        // filter that lost all its results after a USER date change.
        function updateAvailability(showings) {
            mvm.availablePairs = _.map(showings, function (e) {
                return {
                    CinemaID: String(e.CinemaID).trim(),
                    FormatCode: String(e.FormatCode).trim()
                };
            });

            if (resetUnavailableFiltersOnNextBuild) {
                resetUnavailableFiltersOnNextBuild = false;

                //location first: nothing at all at this location today -> clear it
                if (mvm.filterByCinemaLocation) {
                    var cinema = String(mvm.filterByCinemaLocation).trim();
                    var cinemaHasAny = _.some(mvm.availablePairs, function (p) { return p.CinemaID === cinema; });
                    if (!cinemaHasAny) {
                        mvm.filterByCinemaLocation = undefined;
                    }
                }

                //format next (honors whatever location filter survived above):
                //drop any selected format with no results on the new date/location
                if (mvm.selectedFormats && mvm.selectedFormats.length) {
                    mvm.selectedFormats = _.filter(mvm.selectedFormats, function (f) {
                        return mvm.isFormatOptionAvailable(f);
                    });
                }

                //keep the single-select scalar mirror consistent with what survived
                if (!mvm.selectedFormats.length) {
                    mvm.filterByMovieFormat = undefined;
                } else if (!mvm.isFormatSelected(mvm.filterByMovieFormat)) {
                    mvm.filterByMovieFormat = mvm.selectedFormats[0];
                }
            }
        }

        //========================================================================
        // Late-night ("tonight, technically tomorrow") rollover config & helpers
        //========================================================================
        // Showtimes whose actual calendar date is the NEXT day, but whose start time is
        // between midnight (00:00) and this cutoff (EST, inclusive), will ALSO appear on
        // the currently selected day's view with a small date label.  They continue to
        // appear normally on their actual day.
        //   - Set to 4 for "midnight through 4:00 AM inclusive" (current product spec).
        //   - Set to 0 to disable the rollover entirely.
        var LATE_NIGHT_CUTOFF_HOUR = 4;

        // Returns true if a showtime should appear on the selected calendar date.
        //   - Same calendar day = always true.
        //   - Next calendar day with start hour <= LATE_NIGHT_CUTOFF_HOUR (inclusive at :00).
        function showtimeBelongsToSelectedDate(show, selectedDate) {
            if (!show || !selectedDate) return false;
            if (show.Date === selectedDate) return true;
            if (LATE_NIGHT_CUTOFF_HOUR <= 0) return false;

            var nextDay = moment(selectedDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
            if (show.Date !== nextDay) return false;

            var startMoment = moment(show.StartTime);
            if (!startMoment.isValid()) return false;

            var hour = startMoment.hour();
            var minute = startMoment.minute();
            // Inclusive of cutoffHour:00 (e.g., a 4:00 AM show qualifies; 4:01 AM does not).
            if (hour < LATE_NIGHT_CUTOFF_HOUR) return true;
            if (hour === LATE_NIGHT_CUTOFF_HOUR && minute === 0) return true;
            return false;
        }

        // Returns a shallow clone of the showtime, annotated with IsLateNight + DisplayDate
        // when it has rolled forward from the next day.  Cloning prevents the same SHOWTIMES
        // object from being mutated and then mis-rendered on its actual day.
        function annotateShowtimeForSelectedDate(show, selectedDate) {
            if (!show) return show;
            var clone = _.extend({}, show);
            clone.IsLateNight = false;
            clone.DisplayDate = '';
            if (show.Date && show.Date !== selectedDate) {
                var startMoment = moment(show.StartTime);
                clone.IsLateNight = true;
                clone.DisplayDate = startMoment.isValid() ? startMoment.format('M/D') : '';
            }
            return clone;
        }

        //========================================================================
        // Share-link support: keep the hidden share-film inputs in sync with the
        // active filters so the "Share Showtimes" button copies a filtered URL.
        //========================================================================
        $scope.$watch('mvm.filterByCinemaLocation', function (newValue) {
            if (newValue) {
                mvm.filterByCinemaLocation = newValue;
                $("#share-film-cinema").val(newValue);
            } else {
                mvm.filterByCinemaLocation = undefined;
                $("#share-film-cinema").val('');
            }
        });

        // Keep the hidden format input in sync with the effective format filter.
        // Prefers the canonical array (homepage/detail); falls back to the scalar
        // for the legacy single-select views that never populate the array.
        $scope.$watch(function () {
            if (mvm.selectedFormats && mvm.selectedFormats.length) {
                return mvm.selectedFormats.join(',');
            }
            return mvm.filterByMovieFormat || '';
        }, function (newValue) {
            $("#share-film-format").val(newValue || '');
        });

        //========================================================================
        // Calendar strip paging
        //========================================================================
        mvm.next = function () {
            if (mvm.Datelimit + 4 >= mvm.dateArray.length) {
                return;
            }
            mvm.Datelimit += 1;
        };

        mvm.prev = function () {
            if (mvm.Datelimit > 0) {
                mvm.Datelimit -= 1;
            }
        };

        mvm.DisablePrevButton = function () {
            return mvm.Datelimit === 0;
        };

        mvm.DisableNextButton = function () {
            return mvm.Datelimit + 4 >= mvm.dateArray.length;
        };

        mvm.getFilteredItemsVariable = function () {
            return mvm.filteredItemsVariable;
        };

        //========================================================================
        // Watchlist (signed-in C Rewards members, Film Detail only)
        //========================================================================
        mvm.addToWatchlist = function (filmCode, event) {
            event.preventDefault();

            $http.post("/rpc/membership/account/watchlist/add/" + filmCode)
                .then(function (data) {
                    if (data.data.Success == true) {
                        $scope.isInWatchlist = true;
                    } else {
                        alert('Error occurred while adding film to the watchlist.');
                    }
                }).catch(function () {
                    alert('Error occurred while adding film to the watchlist.');
                });
        };

        mvm.removeFromWatchlist = function (filmCode, event) {
            event.preventDefault();

            $http.post("/rpc/membership/account/watchlist/remove/" + filmCode)
                .then(function (data) {
                    if (data.data.Success == true) {
                        $scope.isInWatchlist = false;
                    } else {
                        alert('Error occurred while adding film to the watchlist.');
                    }
                }).catch(function () {
                    alert('Error occurred while adding film to the watchlist.');
                });
        };

        mvm.initWatchlist = function (filmCode) {
            $scope.isInWatchlist = false;
            $scope.showWatchlistLink = false;

            $http.get("/rpc/membership/account/watchlist")
                .then(function (data) {
                    if (data.data.Success == true) {
                        $scope.showWatchlistLink = true;

                        for (var i = 0; i < data.data.Data.length; i++) {
                            var film = data.data.Data[i];
                            if (film.HOFilmCode == filmCode) {
                                $scope.isInWatchlist = true;
                            }
                        }
                    }
                });
        };

        //========================================================================
        // Per-view "filtered items" builders.
        // Each takes mvm.movies (or a sub-collection) and produces
        // mvm.filteredItemsVariable for the view's ng-repeat, restricted to the
        // selected date (including late-night rollover shows).
        //========================================================================

        // Film Detail: one card per cinema showing this film.
        mvm.filteredItemsByFilm = function (items) {
            availabilityUsesLocation = true;
            var result = {};
            mvm.filteredItemsVariable = [];

            angular.forEach(items, function (value, key) {
                delete value['Formats'];
                delete value['undefined'];

                angular.forEach(value, function (value2) {
                    //Includes same-day shows AND next-day early-morning shows (see LATE_NIGHT_CUTOFF_HOUR).
                    if (showtimeBelongsToSelectedDate(value2, mvm.showtimeSelectedDate)) {
                        result[key] = result[key] || [];
                        result[key].push(annotateShowtimeForSelectedDate(value2, mvm.showtimeSelectedDate));
                    }
                });

                if (result[key]) {
                    result[key]['Formats'] = groupFormatsWithType(result[key]);
                }
            });

            $scope.$evalAsync(function () {
                var t = [];
                var arr = _.values(result);

                _.each(result, function (o) { _.forOwn(o['Formats'], function (f) { t = t.concat(f); }); });

                mvm.movieFormatFilterArray = _(t).map('FormatCode').uniq().value().map(Function.prototype.call, String.prototype.trim);

                var cinemaIdsInList = _(t).map('CinemaID').uniq().value().map(Function.prototype.call, String.prototype.trim);

                mvm.cinemaFilterArray = _(mvm.cinemaKeyArray)
                    .keyBy('VistaID')
                    .at(cinemaIdsInList)
                    .value();

                updateAvailability(t);

                var arr2 = [];
                _.each(arr, function (e) {
                    var cinema = mvm.cinemaKey[e[0].CinemaID];
                    var g = _.merge({}, e, cinema);
                    g['FilmID'] = g[0]['FilmID'];
                    g['KeyCombo'] = g.FilmID + g.VistaID;
                    g['CinemaID'] = g['VistaID'];
                    g['MovieFormats'] = Object.keys(e.Formats).map(Function.prototype.call, String.prototype.trim);
                    arr2.push(g);
                });

                mvm.filteredItemsVariable = arr2;
                mvm.hasShowTimes = !_.isEmpty(mvm.filteredItemsVariable);
            });
        };

        // Cinema Detail: one card per film showing at this cinema.
        mvm.filteredItemsCinemaLocation = function (items) {
            availabilityUsesLocation = false; //this page IS one location; ?cinema= is ignored here
            var result = {};
            mvm.filteredItemsVariable = [];

            angular.forEach(items, function (value) {
                var byFilm = _.get(value, 'currentShowtimes.byFilm');

                angular.forEach(byFilm, function (value2, key2) {
                    delete value2['Formats'];
                    delete value2['undefined'];

                    angular.forEach(value2, function (value3) {
                        //Includes same-day shows AND next-day early-morning shows (see LATE_NIGHT_CUTOFF_HOUR).
                        if (showtimeBelongsToSelectedDate(value3, mvm.showtimeSelectedDate)) {
                            result[key2] = result[key2] || [];
                            result[key2].push(annotateShowtimeForSelectedDate(value3, mvm.showtimeSelectedDate));
                        }
                    });

                    if (result[key2]) {
                        result[key2]['Formats'] = groupFormatsWithType(result[key2]);
                    }
                });
            });

            var a = Object.keys(result).map(function (key) {
                var temp = {};
                temp['Title'] = key;
                temp['Formats'] = _.clone(result[key]['Formats']);
                temp['HOFilmCode'] = _.get(result[key], '[0][FilmID]');
                return temp;
            });

            $scope.$evalAsync(function () {
                var arr = _.clone(a);

                var t = [];
                _.each(result, function (o) { _.forOwn(o['Formats'], function (f) { t = t.concat(f); }); });

                mvm.movieFormatFilterArray = _(t).map('FormatCode').uniq().value().map(Function.prototype.call, String.prototype.trim);

                updateAvailability(t);

                var arr2 = [];
                _.each(arr, function (e) {
                    var aItem = _.find(t, function (a2) { return a2.FilmID === e.HOFilmCode; });
                    var g = _.merge({}, e, aItem);
                    g['FilmTitle'] = mvm.filmKey[g.ScheduledFilmId].Title;
                    g['Title'] = mvm.filmKey[g.ScheduledFilmId].Title;
                    g['KeyCombo'] = g.FilmID + g.CinemaID + g.FormatCode;
                    g['ReleaseDateDT'] = mvm.filmKey[g.FilmID].ReleaseDateDT;
                    g['HOFilmCode'] = g.FilmID;
                    arr2.push(g);
                });

                mvm.filteredItemsVariable = arr2;
                mvm.hasShowTimes = !_.isEmpty(mvm.filteredItemsVariable);
            });
        };

        // Double Feature: one card per cinema, one section per film inside it.
        // mvm.movies holds every film in the double feature (one entry per film in the SJO);
        // sections follow the editor's film order from the SJO "lineup" (mvm.lineup).
        // Location/Format options + availability graying are built from the combined set.
        mvm.lineup = [];

        mvm.filteredItemsDoubleFeature = function (movies) {
            availabilityUsesLocation = true;
            var byCinema = {};  // CinemaID -> { HOFilmCode -> [showtimes] }
            var t = [];

            _.each(movies, function (movie) {
                _.forOwn(_.get(movie, 'currentShowtimes.byCinema'), function (shows) {
                    //_.each walks array indexes only, so the extra 'Formats' property is skipped
                    _.each(shows, function (show) {
                        //Includes same-day shows AND next-day early-morning shows (see LATE_NIGHT_CUTOFF_HOUR).
                        if (!showtimeBelongsToSelectedDate(show, mvm.showtimeSelectedDate)) return;

                        var annotated = annotateShowtimeForSelectedDate(show, mvm.showtimeSelectedDate);
                        var cinemaId = annotated.CinemaID;
                        var filmCode = String(annotated.FilmID).trim();

                        byCinema[cinemaId] = byCinema[cinemaId] || {};
                        byCinema[cinemaId][filmCode] = byCinema[cinemaId][filmCode] || [];
                        byCinema[cinemaId][filmCode].push(annotated);
                        t.push(annotated);
                    });
                });
            });

            var filmOrder = _.map(mvm.lineup, function (f) { return String(f.HOFilmCode).trim(); });

            $scope.$evalAsync(function () {
                mvm.movieFormatFilterArray = _(t).map('FormatCode').uniq().value().map(Function.prototype.call, String.prototype.trim);

                var cinemaIdsInList = _(t).map('CinemaID').uniq().value().map(Function.prototype.call, String.prototype.trim);

                mvm.cinemaFilterArray = _(mvm.cinemaKeyArray)
                    .keyBy('VistaID')
                    .at(cinemaIdsInList)
                    .value();

                updateAvailability(t);

                var cards = [];
                _.forOwn(byCinema, function (filmsAtCinema, cinemaId) {
                    var cinema = mvm.cinemaKey[cinemaId];
                    if (!cinema) return;

                    var sections = [];
                    _.forOwn(filmsAtCinema, function (shows, filmCode) {
                        var formats = groupFormatsWithType(shows);
                        var position = filmOrder.indexOf(filmCode);
                        sections.push({
                            HOFilmCode: filmCode,
                            FilmID: filmCode,
                            Title: _.get(mvm.filmKey, [filmCode, 'Title'], ''),
                            Formats: formats,
                            MovieFormats: _.map(formats, function (f) { return String(f[0].FormatCode).trim(); }),
                            SortOrder: position === -1 ? 999 : position
                        });
                    });

                    var card = _.extend({}, cinema);
                    card.CinemaID = cinema.VistaID;
                    card.KeyCombo = 'DF' + cinema.VistaID;
                    card.Films = _.sortBy(sections, 'SortOrder');
                    card.MovieFormats = _.uniq(_.flatten(_.map(card.Films, 'MovieFormats')));
                    cards.push(card);
                });

                mvm.filteredItemsVariable = _.sortBy(cards, 'Title');
                mvm.hasShowTimes = !_.isEmpty(mvm.filteredItemsVariable);
            });
        };

        // Homepage: one poster per film + cinema + format + date combo
        // (the view applies | unique:'ScheduledFilmId' on top).
        mvm.filteredItemsHomePage = function (items) {
            availabilityUsesLocation = true;
            var result = {};
            mvm.filteredItemsVariable = [];

            angular.forEach(items, function (value) {
                var byCinema = _.get(value, 'currentShowtimes.byCinema');

                angular.forEach(byCinema, function (value2, key2) {
                    delete value2['Formats'];
                    delete value2['undefined'];

                    angular.forEach(value2, function (value3) {
                        //Includes same-day shows AND next-day early-morning shows (see LATE_NIGHT_CUTOFF_HOUR).
                        //NOTE: requires the SJO to actually contain the next day's data.  The lightweight
                        //single-day SJO (e.g., "AllShowtimesNestedNoSummarySingleDay") may not include it
                        //until the full SJO finishes loading.  Late-night shows will surface on the next
                        //render after the full SJO replaces the lightweight one.
                        if (showtimeBelongsToSelectedDate(value3, mvm.showtimeSelectedDate)) {
                            result[key2] = result[key2] || [];
                            result[key2].push(annotateShowtimeForSelectedDate(value3, mvm.showtimeSelectedDate));
                        }
                    });

                    if (result[key2]) {
                        result[key2]['Formats'] = _.groupBy(result[key2], 'FormatCode');
                    }
                });
            });

            $scope.$evalAsync(function () {
                var t = [];
                _.each(result, function (o) { _.forOwn(o['Formats'], function (f) { t = t.concat(f); }); });

                mvm.movieFormatFilterArray = _(t).map('FormatCode').uniq().value().map(Function.prototype.call, String.prototype.trim);
                var cinemaIdsInList = _(t).map('CinemaID').uniq().value().map(Function.prototype.call, String.prototype.trim);

                mvm.cinemaFilterArray = _(mvm.cinemaKeyArray)
                    .keyBy('VistaID')
                    .at(cinemaIdsInList)
                    .value();

                updateAvailability(t);

                _.each(t, function (e) {
                    e['FilmTitle'] = mvm.filmKey[e.ScheduledFilmId].Title;
                    e['KeyCombo'] = e.FilmID + e.CinemaID + e.FormatCode;
                    e['PlayingType'] = 'nowplaying';
                    e['ReleaseDateDT'] = mvm.filmKey[e.ScheduledFilmId].ReleaseDateDT;

                    try {
                        e['ReleaseDateDT'] = moment(mvm.filmKey[e.ScheduledFilmId].ReleaseDateDT).unix();
                    } catch (err) { }

                    //convert a "1h 22m" duration into total minutes so the sort dropdown can order by RunTime
                    e['RunTime'] = 0;
                    try {
                        var duration = mvm.filmKey[e.ScheduledFilmId].Duration;
                        if (duration) {
                            var normalized = duration.replace('h', ':').replace('m', '').replace(' ', '');
                            e['RunTime'] = +(normalized.split(':').reduce(function (acc, time) { return (60 * acc) + +time; }));
                        }
                    } catch (err) { }
                });

                mvm.filteredItemsVariable = t;
                mvm.hasShowTimes = !_.isEmpty(mvm.filteredItemsVariable);
            });
        };

        // Groups showings by FormatCode and stamps each entry with a trimmed
        // FormatType (the template renders the format header from [0].FormatType).
        function groupFormatsWithType(showings) {
            var grouped = _.groupBy(showings, 'FormatCode');
            var temp = [];
            _.forOwn(grouped, function (f, v) {
                temp.push(_.map(f, function (o) { return _.extend({ 'FormatType': v.trim() }, o); }));
            });
            return temp;
        }

        //========================================================================
        // Now Playing / Coming Soon tabs (homepage)
        //========================================================================
        mvm.ShowNowPlaying = function () {
            mvm.ComingSoonTab = false;
            mvm.NowPlayingTab = true;
            mvm.filterByPlayingType = 'nowplaying';

            $timeout(function () {
                AOS.refreshHard();
            }, 0);
        };

        mvm.ShowComingSoon = function () {
            mvm.NowPlayingTab = false;
            mvm.ComingSoonTab = true;
            mvm.filterByPlayingType = 'comingsoon';

            $timeout(function () {
                AOS.refreshHard();
            }, 0);
        };

        mvm.getNowPlayingClass = function (name) {
            return (name === mvm.filterByPlayingType) ? 'active' : "";
        };

        // Used by homepage links so film detail opens with the same date/cinema/format filters.
        mvm.routeParams = function () {
            var params = "?date=" + mvm.CURRENT_DATE_URL;
            if (mvm.filterByCinemaLocation) {
                params += "&cinema=" + encodeURIComponent(mvm.filterByCinemaLocation);
            }
            //prefer the canonical multi-format array; fall back to the scalar for the
            //legacy single-select views that never populate it.  Comma-delimited.
            var fmts = (mvm.selectedFormats && mvm.selectedFormats.length)
                ? mvm.selectedFormats
                : (mvm.filterByMovieFormat ? [mvm.filterByMovieFormat] : []);
            if (fmts.length) {
                params += "&format=" + encodeURIComponent(fmts.join(','));
            }
            return params;
        };

        //========================================================================
        // Calendar strip button helpers
        //========================================================================
        mvm.setButtonClass = function ($index) {
            var obj = mvm.dateArray[$index + mvm.Datelimit];
            $scope.selectedTab = moment(obj.Moment).format("YYYY-MM-DD");
        };

        mvm.getButtonClass = function ($index) {
            var obj = mvm.dateArray[$index + mvm.Datelimit];
            return (moment(obj.Moment).format("YYYY-MM-DD") === $scope.selectedTab) ? 'active' : "";
        };

        mvm.getTodaysSelectedTab = function () {
            return moment().format('YYYY-MM-DD');
        };

        mvm.setManualDate = function ($index) {
            var obj = mvm.dateArray[$index + mvm.Datelimit];
            //user-initiated date change: clear filters that have no results on the new date
            resetUnavailableFiltersOnNextBuild = true;
            $scope.setDate(obj.Moment.year(), obj.Moment.month(), obj.Moment.date());
        };

        mvm.DateChange = function (date) {
            //user-initiated date change (datepicker): clear filters that have no results on the new date
            resetUnavailableFiltersOnNextBuild = true;
            var temp = moment(date).format('YYYY-MM-DD');
            mvm.CURRENT_DATE_URL = temp;
            mvm.CURRENT_DATE = moment(temp).format('MMM Do YYYY');
            $scope.$evalAsync(function () {
                $scope.selectedTab = temp.toString();

                var dateindex = _.findIndex(mvm.dateArray, { StringDate: moment(temp).format('YYYY-MM-DD') });
                $("#share-film-date").val(moment(temp).format('YYYY-MM-DD'));
                mvm.Datelimit = dateindex;
            });
            mvm.showtimeSelectedDate = temp;

            mvm.AOSRefresh();
        };

        $scope.setDate = function (year, month, day) {
            $scope.dt = new Date(year, month, day);
            mvm.showtimeSelectedDate = moment().year(year).month(month).date(day).format('YYYY-MM-DD');

            $("#share-film-date").val(mvm.showtimeSelectedDate);

            $scope.$evalAsync(function () {
                $scope.selectedTab = mvm.showtimeSelectedDate.toString();
                mvm.CURRENT_DATE_URL = mvm.showtimeSelectedDate;
                mvm.CURRENT_DATE = moment(mvm.showtimeSelectedDate).format('MMM Do YYYY');
            });

            mvm.AOSRefresh();
        };

        mvm.AOSRefresh = function () {
            // .now-playing-container = the default homepage poster anchor; .cc-cine-poster = the
            // Cinematic homepage view's poster anchor. Clearing aos-animate on both lets EVERY
            // visible poster replay its zoom-in on a filter/date/sort change (without this the
            // Cinematic view only re-animated newly added posters). The extra selector matches
            // nothing on the default view, so this is a no-op there.
            jQuery(".now-playing-container, .cc-cine-poster").removeClass("aos-animate");
            $timeout(function () {
                AOS.refreshHard();
            }, 500);
        };

        mvm.hasShowtimesForSelecedDate = function () {
            return mvm.hasShowTimes;
        };

        mvm.formattedDate = function () {
            return moment($scope.dt).format('MMM Do YYYY');
        };

        $scope.today = function () {
            $scope.dt = new Date();
            mvm.showtimeSelectedDate = moment($scope.dt).format('YYYY-MM-DD');
        };

        $scope.clear = function () {
            $scope.dt = null;
        };

        //========================================================================
        // ui-bootstrap datepicker popup config
        //========================================================================
        var maxCalendarDate = new Date();
        //calendar max date range: 19 months (~1.5 years) ahead
        maxCalendarDate.setMonth(maxCalendarDate.getMonth() + 19);

        $scope.dateOptions = {
            dateDisabled: disabledTest,
            formatYear: 'yy',
            maxDate: maxCalendarDate,
            minDate: null,
            startingDay: 0, // 0 = Sunday first column (client request 2026-10-05; was 1 = Monday)
            showWeeks: false
        };

        var dayDuration = 60 * 60 * 24 * 1000;

        function areDatesEqual(date1, date2) {
            return (parseInt(date1 / dayDuration)) === (parseInt(date2 / dayDuration));
        }

        //Disable all days except days that actually have showtimes
        function disabledTest(data) {
            var date = data.date,
                mode = data.mode;

            var isRealDate = false;

            for (var i = 0; i < mvm.dates.length; i++) {
                var momentDate = moment(mvm.dates[i]).toDate();
                if (areDatesEqual(momentDate, date)) {
                    isRealDate = true;
                }
            }

            return mode === 'day' && !isRealDate;
        }

        $scope.open1 = function () {
            $scope.popup1.opened = true;
        };

        $scope.formats = ['dd-MMMM-yyyy', 'yyyy/MM/dd', 'dd.MM.yyyy', 'shortDate'];
        $scope.format = $scope.formats[0];
        $scope.altInputFormats = ['M!/d!/yyyy'];

        $scope.popup1 = {
            opened: false
        };

        //========================================================================
        // SJO fetching ($http-cached) + localStorage cache for the homepage
        //========================================================================
        function fetchSjoById(sjoid) {
            var deferred = $q.defer();
            $http.get("/rpc/data/sjo/getSJO", { cache: true, params: { id: sjoid } })
                .then(function (response) {
                    if (response && response.data) {
                        deferred.resolve(response.data.Data);
                    }
                })
                .catch(function () {
                    deferred.resolve();
                });
            return deferred.promise;
        }

        function fetchSjoByTitle(sjoid) {
            var deferred = $q.defer();
            $http.get("/rpc/data/sjo/getSJOByTitle", { cache: true, params: { id: sjoid } })
                .then(function (response) {
                    if (response && response.data) {
                        deferred.resolve(response.data.Data);
                    }
                })
                .catch(function () {
                    deferred.resolve();
                });
            return deferred.promise;
        }

        function setWithExpiry(key, value, ttl) {
            const now = new Date();

            //compress: store the big sub-objects as strings (matches what SitefinityToJs expects)
            var compressedValue = value;
            compressedValue.data = JSON.stringify(compressedValue.data);
            compressedValue.calendarDates = JSON.stringify(compressedValue.calendarDates);
            compressedValue.films = JSON.stringify(compressedValue.films);
            compressedValue.dates = JSON.stringify(compressedValue.dates);
            compressedValue.cinemas = JSON.stringify(compressedValue.cinemas);

            const item = {
                value: compressedValue,
                expiry: now.getTime() + ttl,
            };
            localStorage.setItem(key, JSON.stringify(item));
        }

        function getWithExpiry(key) {
            //remove the pre-2022 cache key if it still exists
            const itemStrOld = localStorage.getItem("allsjos");
            if (itemStrOld) {
                localStorage.removeItem("allsjos");
            }

            const itemStr = localStorage.getItem(key);
            if (!itemStr) {
                return null;
            }
            const item = JSON.parse(itemStr);
            const now = new Date();
            if (now.getTime() > item.expiry) {
                localStorage.removeItem(key);
                return null;
            }
            return item.value;
        }

        //========================================================================
        // Entry points (called from the Razor views' ng-init)
        //========================================================================

        function applyShowtimeStatuses(ShowtimeStatuses) {
            mvm.SHOWTIMES_WITH_STATUSES = {};
            if (ShowtimeStatuses) {
                _.each(ShowtimeStatuses, function (s) {
                    mvm.SHOWTIMES_WITH_STATUSES[s.ID] = {
                        soldOut: s.ShowSoldOut,
                        sellingFast: s.TicketsSellingFast
                    };
                });
            }
        }

        // "lineup" on a Double Feature SJO is a pre-serialized array: [{ HOFilmCode, Title, ... }]
        function parseLineup(data) {
            try {
                var parsed = _.isString(data) ? JSON.parse(data) : data;
                var lineup = parsed && parsed.lineup;
                if (_.isString(lineup)) {
                    lineup = JSON.parse(lineup);
                }
                return _.isArray(lineup) ? lineup : [];
            } catch (ex) {
                return [];
            }
        }

        // Film Detail + Cinema Detail: SJO JSON is inlined by Razor.
        $scope.init = function (data, ShowtimeStatuses, FormatOverrides) {
            $scope.today();
            applyShowtimeStatuses(ShowtimeStatuses);

            // FormatOverride emergency feature: server passes a dict of Showtime.Title -> mapped FormatCode
            // for any showtime whose format should be temporarily displayed as something else (e.g.,
            // IMAX 70mm projector outage -> 2D).  We apply this in SitefinityToJs BEFORE the byFormat/
            // byCinema/byFilm groupings so the showtime lands in the correct format-container, picks
            // up the right filter, and matches the right FormatCode header automatically.
            mvm.FORMAT_OVERRIDES = FormatOverrides || {};

            //Double Feature SJOs carry an ordered film lineup; every other SJO leaves this empty
            mvm.lineup = parseLineup(data);

            SitefinityToJs(data);

            mvm.hasShowtimesForSelecedDate();
            mvm.isLoading = false;
        };

        $scope.lazyLoadSetDay = function () {
            $scope.today();
        };

        // Legacy lazy loader (no known view callers in the repo - kept in case a
        // CMS-stored content block still calls it).  Fetches a cinema SJO by its
        // zero-padded Vista ID title.
        $scope.lazyLoadSJOCinemaDetail = function (sjoID, ShowtimeStatuses) {
            $scope.lazyLoadSetDay();

            //cinema SJO titles are the zero-padded Vista cinema IDs
            sjoID = sjoID.toString();
            if (sjoID.length === 1) {
                sjoID = "00" + sjoID;
            }
            if (sjoID.length === 2) {
                sjoID = "00" + sjoID;
            }

            applyShowtimeStatuses(ShowtimeStatuses);

            fetchSjoByTitle(sjoID)
                .then(function (response) {
                    SitefinityToJs(response);
                    mvm.hasShowtimesForSelecedDate();
                    $scope.selectedTab = mvm.getTodaysSelectedTab();
                    mvm.isLoading = false;
                });
        };

        // Homepage: loads the lightweight single-day SJO first for a fast paint,
        // then replaces it with the full SJO (and caches the full SJO in
        // localStorage for 30 minutes).
        $scope.lazyLoadSJOHomepage = function (sjoIDLightweight, sjoIDFull, ShowtimeStatuses) {
            $scope.lazyLoadSetDay();

            applyShowtimeStatuses(ShowtimeStatuses);

            var cacheKey = "sjo-allsjos";
            var cachedItem = getWithExpiry(cacheKey);

            if (cachedItem) {
                SitefinityToJs(cachedItem);
                mvm.hasShowtimesForSelecedDate();
                $scope.selectedTab = mvm.getTodaysSelectedTab();
                mvm.isLoading = false;
            } else {
                fetchSjoById(sjoIDLightweight)
                    .then(function (baseresponse) {
                        SitefinityToJs(baseresponse);
                        mvm.hasShowtimesForSelecedDate();
                        $scope.selectedTab = mvm.getTodaysSelectedTab();

                        fetchSjoById(sjoIDFull)
                            .then(function (response) {
                                try {
                                    if (cacheKey && response) {
                                        var ttlMinutes = 30;
                                        setWithExpiry(cacheKey, response, ttlMinutes * 1000 * 60);
                                    }
                                } catch (err) {
                                    //localStorage full/unavailable - not fatal, just uncached
                                }
                                SitefinityToJs(response);
                                mvm.isLoading = false;
                            });
                    });
            }
        };

        //========================================================================
        // URL query parameters (?date=...&cinema=...&format=...) - share links
        //========================================================================
        function getQueryString(key) {
            var res = {},
                itm = null;
            var qs = location.search.substring(1);
            var pattern = /([^&=]+)=([^&]*)/g;
            while (itm = pattern.exec(qs)) {
                if (key && decodeURIComponent(itm[1]) === key)
                    return decodeURIComponent(itm[2]);
                else if (!key)
                    res[decodeURIComponent(itm[1])] = decodeURIComponent(itm[2]);
            }

            return key ? null : res;
        }

        function applyUrlParameters() {
            var dateparam = getQueryString("date");
            var cinemaparam = getQueryString("cinema");
            var formatparam = getQueryString("format");

            if (dateparam) {
                var tempdate = moment(dateparam);
                if (tempdate && tempdate.isValid()) {
                    //set calendar to this url param date
                    $scope.setDate(tempdate.year(), tempdate.month(), tempdate.date());

                    var dateindex = _.findIndex(mvm.dateArray, { StringDate: moment(tempdate).format('YYYY-MM-DD') });
                    mvm.Datelimit = dateindex;

                    if (mvm.dateArray.length <= 4) {
                        mvm.Datelimit = 0;
                    } else {
                        //keep the strip full when the chosen date is near the end of the range
                        var modifier = mvm.dateArray.length - dateindex;
                        var additionalDatesToShow = 4 - modifier;

                        if (additionalDatesToShow >= 0) {
                            mvm.Datelimit = mvm.Datelimit - additionalDatesToShow;
                        }
                    }
                }
            } else {
                if (mvm.dateArray && mvm.dateArray.length) {
                    var firstdate = _.get(mvm.dateArray, '[0].Moment');
                    if (firstdate && firstdate.isValid()) {
                        $scope.setDate(firstdate.year(), firstdate.month(), firstdate.date());
                        mvm.Datelimit = _.findIndex(mvm.dateArray, { StringDate: moment(firstdate).format('YYYY-MM-DD') });
                    }
                }
            }

            if (cinemaparam) {
                mvm.filterByCinemaLocation = decodeURIComponent(cinemaparam);
                $("#share-film-cinema").val(cinemaparam);
            }

            if (formatparam) {
                //getQueryString already decoded the value.  Support a comma-delimited
                //list (?format=IMAX 2D,IMAX 3D,IMAX 70mm) as well as a single legacy
                //value (?format=IMAX 2D).  Exact match - no family expansion.
                var formats = formatparam.split(',')
                    .map(function (s) { return s.trim(); })
                    .filter(function (s) { return s.length; });

                mvm.selectedFormats = formats;
                //mirror into the scalar so the single-select homepage/legacy views reflect it
                mvm.filterByMovieFormat = formats.length ? formats[0] : undefined;
                $("#share-film-format").val(formats.join(','));
            }
        }

        //========================================================================
        // Format card filter (Film Detail + Cinema Detail)
        //========================================================================
        // A card (one cinema showing a film, or one film at a cinema) is shown when
        // ANY of its formats is in the current selection.  Empty selection = show all.
        // The IMAX / C Premium family expansion was removed 2026-08 in favour of the
        // multi-select dropdown + exact matching (mvm.formatMatchesSelection).
        function matchesFormatFilter(value) {
            if (!mvm.selectedFormats || !mvm.selectedFormats.length) {
                return true;
            }
            var cardFormats = [];
            _.forOwn(value.Formats, function (v) { cardFormats.push(String(v[0].FormatCode).trim()); });
            return _.some(cardFormats, function (code) { return mvm.formatMatchesSelection(code); });
        }

        //Film Detail card filter
        mvm.filterByMultipleMovieFormatsFilmDetail = matchesFormatFilter;

        //Cinema Detail card filter
        mvm.filterByMovieFormatFunction = matchesFormatFilter;

        //Double Feature: a film section shows when one of its formats is selected; a location card
        //shows when at least one of its sections does
        mvm.doubleFeatureSectionFilter = matchesFormatFilter;
        mvm.doubleFeatureCardFilter = function (card) {
            return _.some(card.Films, matchesFormatFilter);
        };

        //referenced from the Film Detail view's filter expression; intentionally a no-op
        mvm.filterByMultipleMovieFormats = function () { };

        //========================================================================
        // SJO JSON -> view models
        //========================================================================
        function SitefinityToJs(data) {
            var now = moment();

            //homepage SJO arrives with its sub-objects as real JSON (not strings); re-stringify
            //so the parsing below is uniform across every SJO variant
            try {
                var testParsed = JSON.parse(data);
                testParsed.data = _.isString(testParsed.data) ? testParsed.data : JSON.stringify(testParsed.data);
                testParsed.calendarDates = _.isString(testParsed.calendarDates) ? testParsed.calendarDates : JSON.stringify(testParsed.calendarDates);
                testParsed.films = _.isString(testParsed.films) ? testParsed.films : JSON.stringify(testParsed.films);
                testParsed.dates = _.isString(testParsed.dates) ? testParsed.dates : JSON.stringify(testParsed.dates);
                testParsed.cinemas = _.isString(testParsed.cinemas) ? testParsed.cinemas : JSON.stringify(testParsed.cinemas);
                data = testParsed;
            } catch (ex) {
                //data was already an object (inlined by Razor) - keep as-is
            }

            var dataParsed = data;
            var updatedData = JSON.parse(dataParsed.data);

            //apply live sold-out/selling-fast statuses + emergency format overrides per showtime
            _.each(updatedData, function (u) {
                delete u['TicketsOnSale'];
                _.forOwn(u['currentShowtimes'], function (g) {
                    delete g['TicketsOnSale'];
                });
                _.forOwn(u['Showtime'], function (g) {
                    delete g['TicketsOnSale'];

                    if (mvm.SHOWTIMES_WITH_STATUSES[g.Title]) {
                        g.ShowSoldOut = mvm.SHOWTIMES_WITH_STATUSES[g.Title].soldOut;
                        g.TicketsSellingFast = mvm.SHOWTIMES_WITH_STATUSES[g.Title].sellingFast;
                    }

                    // FormatOverride: swap FormatCode before the byFormat/byCinema/byFilm groupings
                    // below so the showtime falls into the correct format-container and filter group.
                    // Preserve the original code on OriginalFormatCode + flag IsFormatOverridden so
                    // the bubble template can show an emergency fa-icon indicator.
                    if (mvm.FORMAT_OVERRIDES && g.Title && mvm.FORMAT_OVERRIDES[g.Title]) {
                        var overrideFormat = mvm.FORMAT_OVERRIDES[g.Title];
                        if (overrideFormat && overrideFormat !== g.FormatCode) {
                            g.OriginalFormatCode = g.FormatCode;
                            g.FormatCode = overrideFormat;
                            g.IsFormatOverridden = true;

                            // Detect whether the show's existing event is format-related
                            // (e.g., a "C Premium XL" event icon tied to the C Premium XL 2D
                            // format).  When the EventName is a substring of the ORIGINAL
                            // format code, we treat it as a format event and dim its icon
                            // -- otherwise it would lie to the user (the badge says
                            // "C Premium XL" but the bubble is now in the 2D group).
                            // Non-format events like "Indie Film Series" won't overlap and
                            // stay rendered normally.
                            if (g.EventName && g.OriginalFormatCode) {
                                var evtLower = g.EventName.toLowerCase();
                                var origLower = g.OriginalFormatCode.toLowerCase();
                                if (origLower.indexOf(evtLower) !== -1) {
                                    g.IsFormatRelatedEvent = true;
                                }
                            }
                        }
                    }
                });
            });

            var calDates = JSON.parse(dataParsed.calendarDates);

            //film + cinema lookup dictionaries
            mvm.filmKey = JSON.parse(dataParsed.films);

            _.each(mvm.filmKey, function (u) {
                var tos = _.get(u, 'TicketsOnSale');
                //convert the TicketsOnSale datetime into a simple "on sale yet?" boolean
                u['TicketsOnSale'] = tos ? moment(tos).isBefore(now) : true;
            });

            mvm.cinemaKey = JSON.parse(dataParsed.cinemas);
            mvm.cinemaKeyArray = _.values(mvm.cinemaKey);
            var tempdates = JSON.parse(dataParsed.dates);

            //drop showtimes that are too far in the past to still sell.
            //-11 is ~10 minutes past, so a showing at 12:00pm is still seeable/buyable at 12:10pm per Studio C
            var TimePastShowtimeToKeepShowingShowtime = -11;
            _.each(updatedData, function (e) {
                e.Showtime = _.filter(e.Showtime, function (j) {
                    var diff = moment(j.StartTime).diff(now, 'minutes');
                    return diff >= TimePastShowtimeToKeepShowingShowtime;
                });
            });

            //momentJS conversions
            calDates.forEach(function (el) {
                el['Moment'] = moment(el.Moment);
                el['StringDate'] = moment(el.Moment).format('YYYY-MM-DD');
            });

            //selectable dates: today and later only
            var temparray = [];
            tempdates.forEach(function (d) {
                var datetime = moment(d);
                if (datetime.isSameOrAfter(moment(), 'day')) {
                    temparray.push(d);
                }
            });

            mvm.dates = temparray;
            mvm.dateArray = calDates;

            //rebuild the byFormat/byCinema/byFilm groupings the views consume
            _.each(updatedData, function (m) {
                var temp = {};
                temp['byFormat'] = _.groupBy(m.Showtime, 'FormatCode');
                temp['byCinema'] = _.groupBy(m.Showtime, function (x) { return mvm.cinemaKey[x.CinemaID].Title; });
                temp['byFilm'] = _.groupBy(m.Showtime, function (x) { return mvm.filmKey[x.FilmID].Title; });

                _.forOwn(temp['byCinema'], function (value, key) {
                    temp['byCinema'][key]['Formats'] = _.groupBy(value, 'FormatCode');
                });

                _.forOwn(temp['byFilm'], function (value, key) {
                    temp['byFilm'][key]['Formats'] = _.groupBy(value, 'FormatCode');
                });

                m['currentShowtimes'] = temp;
            });

            //build the calendar strip (Today / Tomorrow / ddd M/D) from the selectable dates
            if (updatedData.length) {
                var tempAry = [];
                var counter = 0;
                _.each(mvm.dates, function (d) {
                    var datetime = moment(d);
                    var obj = {
                        Text: moment(datetime).format("ddd M/D"),
                        Moment: datetime,
                        ID: counter,
                        StringDate: moment(datetime).format('YYYY-MM-DD')
                    };

                    if (datetime.isSame(moment(), "day")) {
                        obj.Text = "Today";
                    }
                    if (datetime.isSame(moment().add(1, 'day'), "day")) {
                        obj.Text = "Tomorrow";
                    }

                    if (!moment(datetime).isBefore(moment(), 'day')) {
                        tempAry.push(obj);
                    }
                    counter++;
                });

                mvm.dateArray = tempAry;

                applyUrlParameters();
            }

            //publish to the views
            ORIGINAL_MOVIES = updatedData.reduce(function (acc, item) {
                acc.push({
                    "FilmId": item.FilmId,
                    "Title": item.Title,
                    "currentShowtimes": item.currentShowtimes
                });
                return acc;
            }, []);

            mvm.movies = _.clone(ORIGINAL_MOVIES, true);
            $timeout(function () { mvm.buildingTimeline = false; }, 0);
            $scope.$evalAsync(mvm.pageLoaded = true);
        }
    }
    MovieController.$inject = ["$scope", "$compile", "$timeout", "$http", "$q"];
    angular
        .module("cstudioApp.controllers")
        .controller("MovieController", MovieController);
})();

angular
    .module("cstudioApp.controllers")
    .filter('toArray', function () {
        return function (obj) {
            if (!(obj instanceof Object)) return obj;
            return _.map(obj, function (val, key) {
                return Object.defineProperty(val, '$key', { __proto__: null, value: key });
            });
        };
    });

angular
    .module("cstudioApp.controllers")
    .filter('unique', function () {
        return function (items, filterOn) {

            if (filterOn === false) {
                return items;
            }

            if ((filterOn || angular.isUndefined(filterOn)) && angular.isArray(items)) {
                var hashCheck = {},
                    newItems = [];

                var extractValueToCompare = function (item) {
                    if (angular.isObject(item) && angular.isString(filterOn)) {
                        return item[filterOn];
                    } else {
                        return item;
                    }
                };

                angular.forEach(items, function (item) {
                    var valueToCheck, isDuplicate = false;

                    for (var i = 0; i < newItems.length; i++) {
                        if (angular.equals(extractValueToCompare(newItems[i]), extractValueToCompare(item))) {
                            isDuplicate = true;
                            break;
                        }
                    }
                    if (!isDuplicate) {
                        newItems.push(item);
                    }

                });
                items = newItems;
            }
            return items;
        };
    });
